import _ from "lodash";
import {getFieldsByPath} from "../liberality/utils";
import {generateFetchFieldListAction} from "../../../../apps/KpModule/actions/api";
import {setFieldVisibility, setFormButtons} from "../../../../apps/KpModule/actions";
import {getDataListList} from "../../../../apps/KpModule/selectors";
import {ObjectId} from "bson";
import {animalEvaluationOptions} from "../staticEntities";
import Errors from "../../../utils/Errors";
import {ageIntervalLabel, findAgeInterval, isFutureBirthDate, parseBirthDate} from "./ageInterval";
import {canPublishAnimals, findActionButton, getAnimalButtons, getNewAnimalButtons, getCUser, isAnimalToValidateByUser, isInternalCUser} from "./utils";
import {getCNDANotificationData} from "../utils/notifications";

// Compatibility targets (types human / animal) used to prefill compatibilityEvaluations.
const COMPATIBILITY_TARGETS_LIST = 'm-C-animal.Animal_compatibilityTargets'

const NOT_EVALUATED = animalEvaluationOptions.find(option => option.id === '3')

// Same limit as the partner website, which only displays 5 photos per animal.
const MAX_PHOTOS = 5

// Species with their age intervals, to show the current age interval as soon as a birth date is picked.
const ANIMAL_TYPE_AGE_INTERVALS_LIST = 'm-C-animal.Animal_animalTypeAgeIntervals'
// Connected user's contact: tells the creation form whether to offer "publish" or "submit for validation"
const CONNECTED_CUSER_LIST = 'm-C-animal.CUser_connectedUser'

function loadedAnimalTypes(state) {
    try {
        return getDataListList(state, ANIMAL_TYPE_AGE_INTERVALS_LIST)
    } catch (error) {
        return null
    }
}

// Shows the read-only age interval below the birth date, computed like the server-side getter.
// The field can't be declared `hidden` (hidden form fields are dropped from the form), so it
// is hidden here until a birth date is picked.
function refreshAgeInterval({store, module}) {
    const {birthDate, animalType, ageInterval} = getFieldsByPath(module.viewMap.form.fields, ['birthDate', 'animalType', 'ageInterval'])
    const date = birthDate.getValue()
    if (!parseBirthDate(date)) {
        store.dispatch(setFieldVisibility(ageInterval.id, false))
        return ageInterval.setValue(null)
    }

    // list not loaded yet: onOpen refreshes once it is
    const animalTypes = loadedAnimalTypes(store.getState())
    if (!animalTypes?.length) return

    store.dispatch(setFieldVisibility(ageInterval.id, true))
    const animalTypeId = animalType.getValue()?.id
    if (!animalTypeId) return ageInterval.setValue("Choisir l'espèce pour calculer la tranche d'âge")

    const ageIntervals = animalTypes.find(type => type.id === animalTypeId)?.ageIntervals
    ageInterval.setValue(
        ageIntervalLabel(findAgeInterval(date, ageIntervals)) || "Aucune tranche d'âge de l'espèce ne correspond à cet âge"
    )
}

// An animal without status is a draft
const isDeletableAnimal = animal => {
    const status = animal?.adoptionStatus
    return ((typeof status === 'string' ? status : status?.id) || 'draft') === 'draft'
}

const isHumanTarget = target => (target.type?.id || target.type) === 'human' ? 1 : 0

// One row per compatibility target (Compatibility of type human/animal), keeping
// the evaluation already entered and defaulting new targets to "Non évaluée".
// Humans first, then by name, so the rows always appear in the same order.
function buildCompatibilityEvaluationRows(targets, rows) {
    const rowByTargetId = new Map(rows.filter(row => row.compatibility?.id).map(row => [row.compatibility.id, row]))
    return [...targets]
        .sort((a, b) => isHumanTarget(b) - isHumanTarget(a)
            || String(a.name || '').localeCompare(String(b.name || ''), 'fr', {sensitivity: 'base'}))
        .map(target => rowByTargetId.get(target.id) || {
            id: new ObjectId().toString(),
            compatibility: {id: target.id, name: target.name},
            evaluation: NOT_EVALUATED
        })
}

export const animalCompatibilityEvaluationEntity = {
    name: 'AnimalCompatibilityEvaluation',
    fields: [
        {path: 'compatibility', type: 'Compatibility'},
        // Emptiness is checked by Animal.validateSave, with a message naming the target.
        {path: 'evaluation', type: 'AnimalEvaluationOption', nullable: true}
    ]
}

export const entity = {
    name: 'Animal',
    fields: [
        {path: 'organization', type: 'Organization'},
        {
            path: 'organizationStatus',
            fieldPath: ['organization.status'],
            $f: (animal, context, callback) => {
                return callback(null, animal?.organization?.status?.name)
            }
        },
        {path: 'establishment', type: 'Shelter'},
        {path: 'animalType', type: 'AnimalType'},
        {path: 'race', type: 'Race'},
        {path: 'size', type: 'AnimalSize'},
        {path: 'sex', type: 'AnimalSex'},
        {path: 'birthDate', type: 'month'},
        {
            path: 'ageInterval',
            fieldPath: ['birthDate', 'animalType.ageIntervals.minAge', 'animalType.ageIntervals.maxAge'],
            $f: (animal, context, callback) => {
                return callback(null, ageIntervalLabel(findAgeInterval(animal.birthDate, animal.animalType?.ageIntervals)))
            }
        },
        {path: 'currentHome', type: 'AnimalHome'},
        {path: 'compatibilityEvaluations', type: 'AnimalCompatibilityEvaluation', link: {type: 'OTM', deleteType: 'cascade', onParent: true, onChild: true}},
        {
            path: 'compatibilitySummary',
            fieldPath: ['compatibilityEvaluations.compatibility.name', 'compatibilityEvaluations.evaluation.id'],
            $f: (animal, context, callback) => {
                const summary = (animal.compatibilityEvaluations || [])
                    .filter(row => row?.compatibility)
                    .map(row => {
                        const evaluation = animalEvaluationOptions.find(option => option.id === row.evaluation?.id) || NOT_EVALUATED
                        return `${row.compatibility.name} : ${evaluation.name}`
                    })
                    .join(' · ')
                return callback(null, summary)
            }
        },
        {path: 'characters', type: 'Character', link: 'MTM', nullable: true},
        {path: 'toleranceToSolitudeEvaluation', type: 'AnimalEvaluationOption'},
        {path: 'handicaps', type: 'Handicap', link: 'MTM', nullable: true},
        {path: 'suitablePlaces', type: 'Compatibility', link: 'MTM'},
        {path: 'hasAdoptionFee', type: 'YesNo'},
        {
            path: 'hasAdoptionFeeBoolean',
            type: 'boolean',
            fieldPath: ['hasAdoptionFee.id'],
            $f: (animal, context, callback) => {
                return callback(null, animal?.hasAdoptionFee?.id === 'yes')
            }
        },
        {path: 'adoptionFee', type: 'decimal'},
        {path: 'adoptionStatus', type: 'AdoptionStatus', nullable: true},
        {path: 'name'},
        {path: 'description'},
        {path: 'history'},
        {path: 'specificInformation'},
        {path: 'images', type: 'Image', link: {type: 'OTM', deleteType: 'cascade'}},
        {
            // Pins at the top of the list the animals waiting for the connected user's validation
            path: '_pinned',
            type: 'boolean',
            fieldPath: ['adoptionStatus.id'],
            $f: function (animal, context, callback) {
                isAnimalToValidateByUser(animal, context)
                    .then(pinned => callback(null, pinned))
                    .catch(callback)
            }
        },
        {
            // Only draft animals can be deleted (hides the delete button of the other lines)
            path: 'noDeleteButtonAccess',
            type: 'boolean',
            fieldPath: ['adoptionStatus.id'],
            f: function () {
                return !isDeletableAnimal(this)
            }
        },
        {
            path: 'buttons',
            fieldPath: ['adoptionStatus.id'],
            $f: function (animal, context, callback) {
                getCUser(context, [])
                    .then(cUser => callback(null, getAnimalButtons(animal?.adoptionStatus?.id, canPublishAnimals(cUser, context))))
                    .catch(callback)
            }
        }
    ],
    filters: [
        {
            name: 'byAdoptionStatus',
            isDefault: false,
            path: 'adoptionStatus',
            tKey: 'status',
            placeholder: 'Sélectionner un statut',
            object: 'AdoptionStatus',
            display: 'name',
            type: 'tags',
            default: [{id: 'draft'}, {id: 'submittedForValidation'}, {id: 'published'}],
            client: true,
            width: 6,
            query: function (context) {
                const statusIds = (_.get(context, 'data.adoptionStatus') || []).map(status => status.id)
                if (!statusIds.length) return {}
                return {adoptionStatus: {$in: statusIds}}
            }
        },
        {
            name: 'byUser',
            isDefault: false,
            async: true,
            query: function(context, callback) {
                getCUser(context, ['shelters.id'])
                    .then(cUser => {
                        const organizationTypeId = cUser?.organization?.organizationType?.id
                        if (organizationTypeId === 'internal') return {}
                        // Association users see the animals of their intervention establishments, or of their whole organization if they have none.
                        if (organizationTypeId === 'association') return {
                            organization: new global.ObjectID(cUser.organization.id),
                            ...(cUser.shelters?.length && {establishment: {$in: cUser.shelters.map(shelter => new global.ObjectID(shelter.id))}})
                        }
                        return {_id: null}
                    })
                    .then(filter => callback(null, filter), callback)
            }
        }
    ],
    validateSave: async function(newObject, oldObject, context, callback) {
        // Status changes only go through the buttons allowed for the stored status and the user
        // (e.g. "publish" is only offered to internal CNDA users).
        try {
            context.animalActionButton = await findActionButton(oldObject, context)
        } catch (error) {
            return callback(error)
        }

        if (isFutureBirthDate(newObject.birthDate)) {
            return callback(new Errors.ValidationError("La date de naissance ne peut pas être dans le futur"))
        }

        if ((newObject.images || []).length > MAX_PHOTOS) {
            return callback(new Errors.ValidationError(`Vous ne pouvez pas ajouter plus de ${MAX_PHOTOS} photos`))
        }

        const missingRows = (newObject.compatibilityEvaluations || [])
            .filter(row => row?.compatibility && !row.evaluation?.id)
        if (!missingRows.length) return callback()

        const targetNames = missingRows.map(row => row.compatibility.name).filter(Boolean)
        return callback(new Errors.ValidationError(
            targetNames.length === missingRows.length
                ? `Veuillez renseigner l'évaluation de compatibilité pour : ${targetNames.join(', ')}`
                : "Veuillez renseigner toutes les évaluations de compatibilité"
        ))
    },
    beforeSave: function(newObject, oldObject, context, callback) {
        const actionButton = context.animalActionButton
        // The status field is read-only: a plain save keeps the stored status (draft for a new animal).
        newObject.adoptionStatus = actionButton
            ? {id: actionButton.nextStatus}
            : (oldObject?.adoptionStatus || {id: 'draft'})
        return callback(null, newObject, oldObject)
    },
    afterSave: function(newObject, oldObject, context, callback) {
        // The validation counter only moves when the status changes (button actions)
        if (context.animalActionButton) refreshCNDANotifications(context)
        return callback()
    },
    validateDelete: function(object, context, callback) {
        if (!isDeletableAnimal(object)) {
            return callback(new Errors.ValidationError("Seuls les animaux au statut « Brouillon » peuvent être supprimés"))
        }
        return callback(null, object)
    },
    afterDelete: function(object, context, callback) {
        refreshCNDANotifications(context)
        return callback()
    }
}

// Pushes the CNDA sidebar counters (incl. animals to validate for internal users) to the connected clients.
function refreshCNDANotifications(context) {
    getCNDANotificationData(context.group.id)
        .then(data => global.ioSocket.emit('afterSave', data))
        .catch(error => console.error('[Animal] notifications refresh failed', error))
}

export const module_ = {
    object: 'Animal',
    accesses: [
        {
            id: COMPATIBILITY_TARGETS_LIST,
            entity: 'Compatibility',
            fieldPath: ['id', 'name', 'type'],
            filters: ['humanOrAnimal']
        },
        {
            id: ANIMAL_TYPE_AGE_INTERVALS_LIST,
            entity: 'AnimalType',
            fieldPath: ['id', 'ageIntervals.minAge', 'ageIntervals.maxAge']
        },
        {
            id: CONNECTED_CUSER_LIST,
            entity: 'CUser',
            fieldPath: ['id', 'canPublishAnimals'],
            filters: ['byUser']
        }
    ],
    tKey: 'mTitle_animalsToAdopt',
    defaultSortBy: 'name',
    defaultSortDirection: 'ASC',
    category: {
        path: 'mTitle_animals',
        icon: 'animal'
    },
    viewMap: {
        dt: [
            'name',
            {path: 'organizationStatus', tKey: 'associationStatus', initiallyNotVisible: true},
            {path: 'organization', tKey: 'association', initiallyNotVisible: true},
            'establishment',
            'animalType',
            'race',
            {path: 'size', tKey: 'adultSize', initiallyNotVisible: true},
            {path: 'sex', initiallyNotVisible: true},
            {path: 'birthDate', initiallyNotVisible: true},
            {path: 'ageInterval', tKey: 'ageInterval'},
            {path: 'currentHome', initiallyNotVisible: true},
            {path: 'toleranceToSolitudeEvaluation', tKey: 'toleranceToSolitude', initiallyNotVisible: true},
            {path: 'compatibilitySummary', tKey: 'compatibility(s)'},
            {path: 'characters', initiallyNotVisible: true},
            {path: 'handicaps', tKey: 'handicap(s)', initiallyNotVisible: true},
            {path: 'suitablePlaces', initiallyNotVisible: true},
            {path: 'hasAdoptionFeeBoolean', tKey: 'adoptionFee', initiallyNotVisible: true},
            {path: 'adoptionStatus', tKey: 'status'},
            {path: '_pinned', hidden: true},
            {path: 'noDeleteButtonAccess', hidden: true}
        ],
        form: {
            fields: [
                {
                    path: 'organization',
                    widthClassName: "col-xs-12 col-sm-6",
                    filters: ['isMemberAssociation', 'visibleByUser'],
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            const fields = getFieldsByPath(module.viewMap.form.fields, ['establishment'])
                            const changedValue = !!oldValue?.id && !!newValue?.id && oldValue.id !== newValue.id
                            if (changedValue) fields?.establishment?.setValue(null)
                            store.dispatch(
                                generateFetchFieldListAction(
                                    "m-C-animal.Animal_establishment",
                                    store.getState,
                                    {
                                        data: {organizationId: newValue?.id}
                                    }
                                )
                            )
                        }
                    }
                },
                {
                    path: 'establishment',
                    widthClassName: "col-xs-12 col-sm-6",
                    required: true,
                    filters: ['byOrganizationAndUser']
                },
                {
                    path: 'animalType',
                    widthClassName: "col-xs-12 col-sm-6",
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            refreshAgeInterval({store, module})
                            store.dispatch(
                                generateFetchFieldListAction(
                                    "m-C-animal.Animal_race",
                                    store.getState,
                                    {
                                        data: {animalType: newValue}
                                    }
                                )
                            )
                        }
                    }
                },
                {
                    path: 'race',
                    widthClassName: "col-xs-12 col-sm-6",
                    filters: ['byAnimalType']
                },
                {path: 'size', tKey: 'adultSize', widthClassName: "col-xs-12 col-sm-6",},
                {path: 'sex', widthClassName: "col-xs-12 col-sm-6",},
                {
                    path: 'birthDate',
                    type: 'monthPicker',
                    required: true,
                    widthClassName: "col-xs-12 col-sm-6",
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => refreshAgeInterval({store, module})
                    }
                },
                {path: 'ageInterval', tKey: 'ageInterval', type: 'readOnly', widthClassName: "col-xs-12 col-sm-6"},
                {path: 'currentHome', widthClassName: "col-xs-12 col-sm-6",},
                {path: 'toleranceToSolitudeEvaluation', tKey: 'toleranceToSolitude', widthClassName: "col-xs-12 col-sm-6",},
                {
                    path: 'compatibilityEvaluations',
                    tKey: 'compatibility',
                    type: 'dtObjects',
                    editable: true,
                    fields: [
                        {path: 'compatibility', tKey: 'compatibilityTarget', type: 'object'},
                        {path: 'evaluation', tKey: 'evaluation', type: 'dropdownObject', customOptions: animalEvaluationOptions, display: 'name', width: 200}
                    ]
                },
                {path: 'characters'},
                {path: 'handicaps', tKey: 'handicap(s)'},
                {path: 'suitablePlaces', filters: ['place']},
                {
                    path: 'hasAdoptionFee',
                    tKey: 'adoptionFee',
                    required: true,
                    default: {id: 'no', name: 'Non'},
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            const fields = getFieldsByPath(module.viewMap.form.fields, ['adoptionFee'])
                            const isYes = newValue?.id === 'yes'
                            store.dispatch(setFieldVisibility(fields?.adoptionFee?.id, isYes))
                            if (!isYes) fields?.adoptionFee?.setValue(0)
                        }
                    }
                },
                {path: 'adoptionFee', tKey: 'amount(€)', type: 'number', numberOfDecimals: 2, wholePositiveNumber: true, required: true, default: 0},
                {path: 'adoptionStatus', tKey: 'adoptionStatus', disabled: true},
                {path: 'name', tKey: 'animalName', required: true},
                {path: 'description', type: 'textarea', required: true},
                {path: 'history', type: 'textarea'},
                {path: 'specificInformation', tKey: 'specificInformation', type: 'textarea'},
                {path: 'images', tKey: 'photos', placeholder: 'addPhoto(s)', type: 'image', fieldPath: ['id', 'name', 'user.id', 'user.name', 'date'], required: true},
                {path: 'buttons', hidden: true},
            ],
            dataLists: [COMPATIBILITY_TARGETS_LIST, ANIMAL_TYPE_AGE_INTERVALS_LIST, CONNECTED_CUSER_LIST],
            onOpen: ({store, module}) => {
                // A new animal gets the draft actions right away (no need to save it as a draft first)
                if (store.getState().ui.objectMode === 'newObject') {
                    store.dispatch(generateFetchFieldListAction(CONNECTED_CUSER_LIST, store.getState))
                        .then(response => {
                            if (response?.error) return console.error('[Animal] connected user fetch failed', response)
                            const canPublish = !!response?.data?.[0]?.canPublishAnimals
                            store.dispatch(setFormButtons(getNewAnimalButtons(canPublish)))
                        })
                        .catch(error => console.error('[Animal] connected user fetch failed', error))
                }
                const {ageInterval} = getFieldsByPath(module.viewMap.form.fields, ['ageInterval'])
                store.dispatch(setFieldVisibility(ageInterval.id, false))
                store.dispatch(generateFetchFieldListAction(ANIMAL_TYPE_AGE_INTERVALS_LIST, store.getState))
                    .then(() => refreshAgeInterval({store, module}))
                    .catch(error => console.error('[Animal] age intervals fetch failed', error))
                try {
                    const {compatibilityEvaluations} = getFieldsByPath(module.viewMap.form.fields, ['compatibilityEvaluations'])
                    store.dispatch(generateFetchFieldListAction(COMPATIBILITY_TARGETS_LIST, store.getState))
                        .then(response => {
                            if (response?.error) return console.error('[Animal] compatibility targets fetch failed', response)
                            compatibilityEvaluations.setValue(
                                buildCompatibilityEvaluationRows(response?.data || [], compatibilityEvaluations.getValue() || [])
                            )
                        })
                        .catch(error => console.error('[Animal] compatibility prefill failed', error))
                } catch (error) {
                    console.error('[Animal] compatibility prefill failed', error)
                }
            }
        }
    },
    filters: ['byAdoptionStatus', 'byUser']
}
